/**
 * Service de gestion des signatures électroniques pour les documents
 * Gère la capture de signatures, la validation et l'horodatage
 */

/**
 * Récupère l'adresse IP du client (simplifié)
 * Note: Pour une vraie implémentation, utiliser une API comme ipify.org
 * @returns {Promise<string>} - Adresse IP
 */
export const getClientIpAddress = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'IP:', error);
    return 'IP non disponible';
  }
};

/**
 * Valide une signature (vérifie qu'elle n'est pas vide)
 * @param {string} signatureDataUrl - Signature en format data URL
 * @returns {boolean} - True si la signature est valide
 */
export const validateSignature = (signatureDataUrl) => {
  if (!signatureDataUrl || signatureDataUrl.trim() === '') {
    return false;
  }

  // Vérifier que ce n'est pas juste une image vide
  // Une signature canvas vide commence généralement par une image blanche
  const emptyCanvasPattern = /^data:image\/png;base64,iVBORw0KGgoAAAANSUhEUgAA[A-Za-z0-9+/=]{50,100}$/;

  if (emptyCanvasPattern.test(signatureDataUrl)) {
    return false;
  }

  return true;
};

/**
 * Crée un objet signature avec toutes les métadonnées
 * @param {string} signatureImage - Image de la signature (data URL)
 * @param {string} signedBy - Nom du signataire
 * @param {string} ipAddress - Adresse IP du signataire
 * @returns {Object} - Objet signature complet
 */
export const createSignatureObject = (signatureImage, signedBy, ipAddress = null) => {
  return {
    signatureImage,
    signedBy,
    signedAt: new Date().toISOString(),
    ipAddress: ipAddress || 'Non disponible',
    timestamp: Date.now(),
    browserInfo: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language
    }
  };
};

/**
 * Convertit une image de signature en format optimisé
 * @param {string} signatureDataUrl - Signature en data URL
 * @param {number} maxWidth - Largeur maximale (par défaut 400px)
 * @param {number} maxHeight - Hauteur maximale (par défaut 200px)
 * @returns {Promise<string>} - Image redimensionnée en data URL
 */
export const optimizeSignatureImage = (signatureDataUrl, maxWidth = 400, maxHeight = 200) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Créer un canvas pour redimensionner
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculer les nouvelles dimensions en conservant le ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir en data URL
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      reject(new Error('Erreur lors du chargement de l\'image de signature'));
    };

    img.src = signatureDataUrl;
  });
};

/**
 * Génère un hash de la signature pour vérification d'intégrité
 * @param {string} signatureDataUrl - Signature en data URL
 * @returns {Promise<string>} - Hash SHA-256 de la signature
 */
export const generateSignatureHash = async (signatureDataUrl) => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(signatureDataUrl);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Erreur lors de la génération du hash:', error);
    return null;
  }
};

/**
 * Vérifie l'intégrité d'une signature en comparant son hash
 * @param {string} signatureDataUrl - Signature à vérifier
 * @param {string} expectedHash - Hash attendu
 * @returns {Promise<boolean>} - True si l'intégrité est vérifiée
 */
export const verifySignatureIntegrity = async (signatureDataUrl, expectedHash) => {
  try {
    const currentHash = await generateSignatureHash(signatureDataUrl);
    return currentHash === expectedHash;
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'intégrité:', error);
    return false;
  }
};

/**
 * Crée une signature certifiée avec hash et horodatage
 * @param {string} signatureImage - Image de la signature
 * @param {string} signedBy - Nom du signataire
 * @param {Object} documentData - Données du document signé
 * @returns {Promise<Object>} - Signature certifiée
 */
export const createCertifiedSignature = async (signatureImage, signedBy, documentData) => {
  try {
    // Récupérer l'IP
    const ipAddress = await getClientIpAddress();

    // Optimiser l'image
    const optimizedImage = await optimizeSignatureImage(signatureImage);

    // Générer le hash
    const signatureHash = await generateSignatureHash(optimizedImage);

    // Créer l'objet signature
    const signature = {
      signatureImage: optimizedImage,
      signedBy,
      signedAt: new Date().toISOString(),
      ipAddress,
      timestamp: Date.now(),
      browserInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      },
      documentInfo: {
        documentId: documentData.id,
        documentType: documentData.documentType,
        documentName: documentData.documentName
      },
      integrity: {
        hash: signatureHash,
        algorithm: 'SHA-256'
      }
    };

    return signature;
  } catch (error) {
    console.error('Erreur lors de la création de la signature certifiée:', error);
    throw error;
  }
};

/**
 * Génère un certificat de signature au format texte
 * @param {Object} signatureData - Données de la signature
 * @returns {string} - Certificat au format texte
 */
export const generateSignatureCertificate = (signatureData) => {
  const {
    signedBy,
    signedAt,
    ipAddress,
    browserInfo,
    documentInfo,
    integrity
  } = signatureData;

  return `
=======================================================
     CERTIFICAT DE SIGNATURE ÉLECTRONIQUE
=======================================================

Document signé : ${documentInfo?.documentName || 'N/A'}
Type de document : ${documentInfo?.documentType || 'N/A'}

Signataire : ${signedBy}
Date et heure : ${new Date(signedAt).toLocaleString('fr-FR')}
Adresse IP : ${ipAddress}

Informations du navigateur :
- User Agent : ${browserInfo?.userAgent || 'N/A'}
- Plateforme : ${browserInfo?.platform || 'N/A'}
- Langue : ${browserInfo?.language || 'N/A'}

Intégrité :
- Algorithme : ${integrity?.algorithm || 'N/A'}
- Hash : ${integrity?.hash || 'N/A'}

Ce document a été signé électroniquement conformément
aux dispositions légales en vigueur.

=======================================================
`;
};

/**
 * Exporte la signature en format JSON
 * @param {Object} signatureData - Données de la signature
 * @returns {string} - JSON formatté
 */
export const exportSignatureAsJSON = (signatureData) => {
  return JSON.stringify(signatureData, null, 2);
};

/**
 * Télécharge le certificat de signature
 * @param {Object} signatureData - Données de la signature
 * @param {string} fileName - Nom du fichier (optionnel)
 */
export const downloadSignatureCertificate = (signatureData, fileName = null) => {
  const certificate = generateSignatureCertificate(signatureData);
  const blob = new Blob([certificate], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `certificat_signature_${Date.now()}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Valide toutes les conditions pour accepter une signature
 * @param {string} signatureDataUrl - Signature à valider
 * @param {string} signedBy - Nom du signataire
 * @returns {Object} - Résultat de la validation
 */
export const validateSignatureRequirements = (signatureDataUrl, signedBy) => {
  const errors = [];

  // Vérifier la signature
  if (!validateSignature(signatureDataUrl)) {
    errors.push('La signature est vide ou invalide');
  }

  // Vérifier le nom du signataire
  if (!signedBy || signedBy.trim().length < 2) {
    errors.push('Le nom du signataire doit contenir au moins 2 caractères');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Ajoute un watermark invisible à la signature (steganographie simple)
 * @param {string} signatureDataUrl - Signature originale
 * @param {string} watermarkText - Texte du watermark
 * @returns {Promise<string>} - Signature avec watermark
 */
export const addInvisibleWatermark = (signatureDataUrl, watermarkText) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = img.width;
      canvas.height = img.height;

      // Dessiner l'image originale
      ctx.drawImage(img, 0, 0);

      // Ajouter un watermark invisible (modification très subtile des pixels)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Encoder le texte dans les bits de poids faible
      const watermarkBytes = new TextEncoder().encode(watermarkText);

      for (let i = 0; i < watermarkBytes.length && i < data.length / 4; i++) {
        const byte = watermarkBytes[i];
        for (let bit = 0; bit < 8; bit++) {
          const pixelIndex = (i * 8 + bit) * 4;
          if (pixelIndex < data.length) {
            const bitValue = (byte >> bit) & 1;
            data[pixelIndex] = (data[pixelIndex] & 0xFE) | bitValue;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      reject(new Error('Erreur lors de l\'ajout du watermark'));
    };

    img.src = signatureDataUrl;
  });
};

export default {
  getClientIpAddress,
  validateSignature,
  createSignatureObject,
  optimizeSignatureImage,
  generateSignatureHash,
  verifySignatureIntegrity,
  createCertifiedSignature,
  generateSignatureCertificate,
  exportSignatureAsJSON,
  downloadSignatureCertificate,
  validateSignatureRequirements,
  addInvisibleWatermark
};
